import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { generatePath, NavLink } from "react-router-dom";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import Headline from "~/components/Headline";
import Hint from "~/components/Hint";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { INVOICE_DETAILS_PATH } from "~/constants/appRoutes.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import type { InvoiceRecipientType } from "~/modules/billing/api/invoiceRecipient/invoiceRecipientTypes.ts";
import { invoiceTypeId as invoiceTypeIds } from "~/modules/billing/api/invoiceType.ts";
import {
	updateInvoiceRecipientInformation,
	updateInvoiceSenderInformation,
} from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import { invoiceRecipientQuery } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import type { ClientContactPersonType } from "~/modules/client/api/clientContactPerson/clientContactPersonTypes.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	recipientOrSenderInformation: string;
}

type Props = {
	invoiceData: Invoice
	onCancel: () => void;
	onSuccess: () => void;
};

const UpdateInvoiceRecipientForm = ({
	invoiceData,
	onCancel,
	onSuccess,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const isProjectInvoice = invoiceData.isProjectInvoice;
	const invoiceTypeId = invoiceData.invoiceTypeId;
	const isSelfBilledInvoice = isProjectInvoice && invoiceTypeId === invoiceTypeIds.SelfBilledInvoice;
	const isReversalInvoice = invoiceTypeId === invoiceTypeIds.ReversalInvoice;
	const isRegularProjectInvoice = isProjectInvoice && !isSelfBilledInvoice && !isReversalInvoice;
	const isAdvanceInvoice = isProjectInvoice && invoiceTypeId === invoiceTypeIds.AdvanceInvoice;


	const {
		data: invoiceRecipientSummaryData,
		status: invoiceRecipientSummaryStatus,
		isLoading: invoiceRecipientSummaryIsLoading,
	} = useQuery(invoiceRecipientQuery(isProjectInvoice ? invoiceData.id : ""));

	const schema = useMemo(() => {
		return yup.object({
			recipientOrSenderInformation: yup.string().required(),
		});
	}, []);

	const defaultValues = useMemo(() => {
		let recipientOrSenderInformation = invoiceData.recipientInformation || "";
		if (isSelfBilledInvoice) {
			recipientOrSenderInformation = invoiceData.senderInformation || "";
		}

		return {
			recipientOrSenderInformation,
		};
	}, [invoiceData.recipientInformation, invoiceData.senderInformation, isSelfBilledInvoice]);
	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
		setValue,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
		enableInitially: false,
	});


	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			if (isSelfBilledInvoice) {
				await updateInvoiceSenderInformation({
					invoiceId: invoiceData.id,
					senderInformation: data.recipientOrSenderInformation,
				});
			} else {
				await updateInvoiceRecipientInformation({
					invoiceId: invoiceData.id,
					recipientInformation: data.recipientOrSenderInformation,
				});
			}

			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [invoiceData.id, isSelfBilledInvoice, onSuccess]);

	const getRecipientInformation = useCallback((recipientData: {
		clientContactPerson: ClientContactPersonType | null;
		invoiceRecipient: InvoiceRecipientType | null;
	}) => {
		if (recipientData.invoiceRecipient) {
			const emailPart = recipientData.invoiceRecipient.email || "";
			const fullName = recipientData.invoiceRecipient.displayName || "";
			return `${fullName}${emailPart && fullName ? ", " : ""}${emailPart}`;
		}

		if (recipientData.clientContactPerson) {
			const emailPart = recipientData.clientContactPerson.emailBusiness || "";
			const fullName = recipientData.clientContactPerson.fullName || "";
			return `${fullName}${emailPart && fullName ? ", " : ""}${emailPart}`;
		}
	}, []);

	const orderRecipientString = !invoiceRecipientSummaryData ? null : getRecipientInformation(invoiceRecipientSummaryData.order);
	const quotaRecipientString = !invoiceRecipientSummaryData ? null : getRecipientInformation(invoiceRecipientSummaryData.quota);

	const handleUseValueClick = useCallback((value:string) => {
		setValue("recipientOrSenderInformation", value, { shouldDirty: true, shouldTouch: true, shouldValidate:true });

	}, [setValue]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>{isSelfBilledInvoice ? "Rechnungssteller" : "Rechnungsempfänger"} bearbeiten</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<div className="space-y-2">
					{!isSelfBilledInvoice &&
						<p className="text-sm leading-tight text-gray-500">Bitte angeben auf welchem Wege die
							Rechnung an den Kunden übermittelt wurde / werden soll
							(i.d.R. <span className="whitespace-nowrap">E-Mail Adresse</span>). Die Rechnungsadresse für
							das Rechnungsdokument wird unabhängig hiervon aus der Bestellung übernommen.</p>}
					{isSelfBilledInvoice &&
						<p className="text-sm leading-tight text-gray-500">Bitte angeben auf welchem Wege die
							Rechnung vom Kunden übermittelt wurde (i.d.R. <span className="whitespace-nowrap">E-Mail Adresse</span>).
						</p>}
					<Input control={control}
						   name="recipientOrSenderInformation"
						   placeholder="Name, E-Mail Addresse, Versandart..." />
				</div>
				{isProjectInvoice && <div className="mt-4">
					<Headline type="h4">Information übernehmen aus...</Headline>
					{invoiceRecipientSummaryIsLoading &&
						<div className="mt-4 flex items-center justify-center gap-x-1 text-sm text-gray-600">
							<LoadingSpinner />Lade Quellen...</div>}

					{invoiceRecipientSummaryStatus === "error" &&
						<p className="mt-1 text-sm text-red-500">Die Informationen konnten nicht geladen werden.</p>}
					{invoiceRecipientSummaryStatus === "success" && <div className="mt-2 space-y-4">
						{!isReversalInvoice && invoiceRecipientSummaryData.previousInvoice && <div>
							<div className="font-bold">Vorheriger
								Rechnung <NavLink className="hover:text-secondary-500"
												  to={generatePath(INVOICE_DETAILS_PATH, { invoiceId: invoiceRecipientSummaryData.previousInvoice.id })}>({invoiceRecipientSummaryData.previousInvoice.invoiceNumber})</NavLink>
							</div>
							<div className="rounded border border-gray-300 p-2">
								<div className="flex items-center justify-between">
									<span className="text-sm"> {invoiceRecipientSummaryData.previousInvoice.recipientInformation}</span>
									<Button size="sm"
											theme="primary"
											onClick={() => handleUseValueClick(invoiceRecipientSummaryData.previousInvoice?.recipientInformation || "")}
											disabled={!invoiceRecipientSummaryData.previousInvoice.recipientInformation}>Übernehmen</Button>
								</div>
							</div>
						</div>}

						{isReversalInvoice && invoiceRecipientSummaryData.reversedInvoice && <div>
							<div className="font-bold">Stornierter
								Rechnung <NavLink className="hover:text-secondary-500"
												  to={generatePath(INVOICE_DETAILS_PATH, { invoiceId: invoiceRecipientSummaryData.reversedInvoice.id })}>({invoiceRecipientSummaryData.reversedInvoice.invoiceNumber})</NavLink>
							</div>
							<div className="rounded border border-gray-300 p-2">
								<div className="flex items-center justify-between">
									<span className="text-sm"> {invoiceRecipientSummaryData.reversedInvoice.recipientInformation}</span>
									<Button size="sm"
											theme="primary"
											onClick={() => handleUseValueClick(invoiceRecipientSummaryData.reversedInvoice?.recipientInformation || "")}
											disabled={!invoiceRecipientSummaryData.reversedInvoice.recipientInformation}>Übernehmen</Button>
								</div>
							</div>
						</div>}
						{isAdvanceInvoice && <div>
							<div className="font-bold">Abrechnungskontingent</div>
							<div className="rounded border border-gray-300 p-2">
								<div className="flex items-center justify-between">
									<span className="text-sm">{quotaRecipientString}</span>
									<Button size="sm"
											theme="primary"
											onClick={() => handleUseValueClick(quotaRecipientString || "")}
											disabled={!quotaRecipientString}>Übernehmen</Button>
								</div>
							</div>
						</div>}
						{(isRegularProjectInvoice || isSelfBilledInvoice) && <div>
							<div className="font-bold">Bestellung
							</div>
							{invoiceRecipientSummaryData.hasMultipleOrders && <Hint theme="warning"
																					size="sm"
							>Diese Rechnung
								basiert auf mehreren Bestellungen, daher konnte keine eindeutige
								Empfängerinformation auf Basis der Bestellungen ermittlelt werden.</Hint>}
							{!invoiceRecipientSummaryData.hasMultipleOrders &&
								<div className="rounded border border-gray-300 p-2">

									<div className="flex items-center justify-between">
										<span className="text-sm">{orderRecipientString}</span>
										<Button size="sm"
												theme="primary"
												onClick={() => handleUseValueClick(orderRecipientString || "")}
												disabled={!orderRecipientString}>Übernehmen</Button>
									</div>
								</div>}
						</div>}
					</div>}
				</div>}

			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					{t("form:buttonTexts.save")}
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateInvoiceRecipientForm;
